import { Grid, rem } from "@mantine/core";
import { CheckListType } from "src/components/pages/Settings/Miscellaneous/CheckLists/CheckLists.types";
import { StyledGridCol } from "src/components/pages/Summary/VerifyEntries/VerifyEntries.styled";
import LocalSearch from "src/components/shared/LocalSearch/LocalSearch";
import { Select } from "src/components/shared/MantineCombobox";
import { checkBaseField } from "src/generator/utils/getCheckListProgress";
import {
  type ICheckList,
  type ICheckListItem,
  type SetChecklist,
} from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { Flex } from "src/styled";

import ChecklistListRow from "./ChecklistListRow";
import { checklistTypes, EChecklistType } from "./checklists.utils";
import styles from "./ChecklistsModal.module.scss";

const hasProgress = (row: ICheckList): boolean =>
  row.data.type === CheckListType.Group
    ? (row.data.list as unknown as { items?: ICheckListItem[] }[]).some(sub =>
        (sub.items ?? []).some(item => checkBaseField(item) === 1),
      )
    : row.data.list.some(item => checkBaseField(item) === 1);

interface Props {
  data: ICheckList[] | undefined;
  defaultData: ICheckList[] | undefined;
  setSearchedData: (data: ICheckList[]) => void;
  setCheckList: SetChecklist;
  loading: boolean;
  currentLbid: string | undefined;
  refetch: () => void;
  disableCopy: boolean;
  checklistType: EChecklistType | null;
  setChecklistType: (type: EChecklistType) => void;
  resetSearch: number;
  setResetSearch: (resetSearch: number) => void;
  // Keep rows clickable to view read-only even if checklists are disabled for the current yacht
  viewOnly?: boolean;
}

const headers = [
  { label: "Name", colSpan: 8 },
  { label: "Version", colSpan: 1 },
  { label: "", colSpan: 2 },
];

const CheckListsList = ({
  data,
  checklistType,
  setChecklistType,
  setSearchedData,
  defaultData,
  resetSearch,
  setResetSearch,
  viewOnly,
  ...props
}: Props) => {
  const { areChecklistsDisabled } = useSelector(state => state.permsSlice);
  const disabled = viewOnly ? false : areChecklistsDisabled;

  return (
    <Flex direction="column" gap={rem(20)}>
      <Flex gap={2} align="flex-end">
        {checklistType && (
          <Select
            value={checklistType}
            onChange={setChecklistType}
            label="Checklist Type"
            data={checklistTypes}
            w={200}
          />
        )}
        <LocalSearch
          setData={setSearchedData}
          defaultData={defaultData}
          searchByProperty="name"
          resetSearch={resetSearch}
          setResetSearch={setResetSearch}
        />
      </Flex>

      <Flex direction="column" className={styles.listSection}>
        <Grid align="center" className={styles.header}>
          {headers.map((c, i) => (
            <StyledGridCol
              span={c.colSpan}
              align="center"
              key={`checklist-header-${i}`}>
              {c.label}
            </StyledGridCol>
          ))}
        </Grid>
        <div className={styles.rowList}>
          {data &&
          data.filter(c => !c.hidden && (!viewOnly || hasProgress(c))).length >
            0 ? (
            data.map(
              (c, i) =>
                !c.hidden &&
                (!viewOnly || hasProgress(c)) && (
                  <ChecklistListRow
                    key={`checkist-lb-${i}`}
                    row={c}
                    disabled={disabled}
                    {...props}
                  />
                ),
            )
          ) : (
            <div style={{ textAlign: "center" }}>
              {checklistType === EChecklistType.PROGRESS
                ? "There are no outstanding Checklists for completion"
                : "There are no matching checklists"}
            </div>
          )}
        </div>
      </Flex>
    </Flex>
  );
};

export default CheckListsList;
